import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Monitor, Smartphone, Tablet, MapPin, Phone } from "lucide-react";

function DeviceIcon({ type }) {
  if (type === "Mobile") return <Smartphone size={12} />;
  if (type === "Tablet") return <Tablet size={12} />;
  return <Monitor size={12} />;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function OnlineNowPanel({ onUserClick }) {
  const [data, setData] = useState({ online: [], away: [], online_count: 0, away_count: 0 });
  const [showAway, setShowAway] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.get("/admin/online?window_min=5")
        .then(({ data }) => { if (!cancelled) setData(data); })
        .catch(() => {});
    load();
    const iv = setInterval(load, 10000); // refresh every 10s
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const rows = showAway ? [...data.online, ...data.away] : data.online;

  return (
    <div className="bg-white border border-brand-subtle rounded-2xl p-5" data-testid="admin-online-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 brand-forest">
          <Users size={18} />
          <span className="label-eyebrow text-gray-500">Live users</span>
        </div>
        <div className="flex gap-4 text-xs">
          <span className="flex items-center gap-1.5 text-emerald-600 font-bold" data-testid="online-count">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse" />
            {data.online_count} online
          </span>
          <button
            onClick={() => setShowAway((s) => !s)}
            className="text-amber-600 font-bold hover:underline"
            data-testid="away-toggle"
          >
            {data.away_count} away
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No active users right now.</p>
      ) : (
        <div className="divide-y divide-brand-subtle">
          {rows.map((u) => {
            const isOnline = data.online.find((o) => o.id === u.id);
            return (
              <button
                key={u.id}
                onClick={() => onUserClick?.(u.id)}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-[#FBF6EC] transition-colors px-2 -mx-2 rounded-lg"
                data-testid={`online-user-${u.id}`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]" : "bg-amber-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{u.name || u.email}</p>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-2 flex-wrap">
                    {u.phone && (
                      <span className="inline-flex items-center gap-1 text-gray-700 font-medium"><Phone size={10} /> {u.phone}</span>
                    )}
                    <span className="inline-flex items-center gap-1"><DeviceIcon type={u.device} /> {u.browser || "—"} · {u.os || "—"}</span>
                    {(u.city || u.state) && (
                      <span className="inline-flex items-center gap-1"><MapPin size={10} /> {[u.city, u.state].filter(Boolean).join(", ")}</span>
                    )}
                  </p>
                </div>
                <span className="text-[10px] text-gray-400 shrink-0">{timeAgo(u.last_seen_at)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
